import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { Send, Loader2, AlertCircle, Bot, Copy, Check } from '@/ui/icons'
import { Button, Textarea } from '@/ui/components'
import { useSubscription } from '@/modules/subscriptions/hooks/useSubscription'
import { UpgradePrompt } from '@/modules/subscriptions/components/UpgradePrompt'
import { RangerMarkdownContent } from './RangerMarkdownContent'
import { RangerFunctionResultCard } from './RangerFunctionResultCard'
import { RangerSuggestions } from './RangerSuggestions'
import type { RangerChatMessage } from '../hooks/useRanger'

const TEXTAREA_MAX_HEIGHT_PX = 120

interface RangerChatProps {
  tripId?: string
  messages: RangerChatMessage[]
  sendMessage: (message: string) => Promise<void>
  loading: boolean
  error: string | null
  clearError: () => void
}

export function RangerChat({ tripId, messages, sendMessage, loading, error, clearError }: RangerChatProps) {
  const { status } = useSubscription()
  const [input, setInput] = useState('')
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const canUseRanger = status?.features.canUseRanger ?? false

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  useEffect(() => {
    textareaRef.current?.focus()
  }, [])

  // Auto-grow the textarea with content, capped so it can't swallow the whole panel
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, TEXTAREA_MAX_HEIGHT_PX)}px`
  }, [input])

  const submitMessage = async (text: string) => {
    if (!text.trim() || loading || !canUseRanger) return
    setInput('')
    try {
      await sendMessage(text.trim())
    } finally {
      textareaRef.current?.focus()
    }
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    submitMessage(input)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      submitMessage(input)
    }
  }

  const handleCopy = async (message: RangerChatMessage) => {
    try {
      await navigator.clipboard.writeText(message.content)
      setCopiedId(message.id)
      setTimeout(() => setCopiedId((current) => (current === message.id ? null : current)), 1500)
    } catch {
      // Clipboard permission denied — no confirmation to show, just skip it.
    }
  }

  if (!canUseRanger) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex-1 overflow-y-auto p-4">
          <UpgradePrompt
            title="Ranger AI Assistant"
            message="Ranger AI Assistant provides expert trip planning advice for overlanding and camping adventures. Upgrade to Pro to access this feature."
            feature="ranger"
            currentTier={status?.tier ?? 'free'}
            variant="default"
            className="mt-4"
          />
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center px-2 text-center">
            <div className="mb-4 rounded-full bg-adventure-100 p-4">
              <Bot className="h-8 w-8 text-adventure-600" />
            </div>
            <h3 className="mb-1.5 text-lg font-semibold text-gray-900 dark:text-foreground">
              {tripId ? "Let's dial in this trip" : 'Ask Ranger anything'}
            </h3>
            <p className="mb-6 max-w-xs text-sm text-gray-600 dark:text-earth-400">
              {tripId
                ? 'Ranger can see your waypoints, dates, and crew — ask for routes, gear, or a gut check on the plan.'
                : 'Routes, gear, weather, safety — Ranger thinks like a trail leader, not a search engine.'}
            </p>
            <RangerSuggestions tripId={tripId} onSelect={submitMessage} disabled={loading} />
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {messages.map((message) => (
              <div key={message.id} className={message.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                {message.role === 'user' ? (
                  <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-adventure-600 px-4 py-2.5 text-sm text-white">
                    <p className="whitespace-pre-wrap break-words">{message.content}</p>
                  </div>
                ) : (
                  <div className="group w-full max-w-[92%]">
                    <div className="mb-1 flex items-center gap-1.5">
                      <Bot className="h-3.5 w-3.5 text-adventure-600" />
                      <span className="text-xs font-medium text-gray-500 dark:text-earth-400">Ranger</span>
                      <button
                        type="button"
                        onClick={() => handleCopy(message)}
                        aria-label="Copy response"
                        className="ml-auto text-gray-400 opacity-0 transition-opacity hover:text-gray-700 group-hover:opacity-100 dark:hover:text-foreground"
                      >
                        {copiedId === message.id ? (
                          <Check className="h-3.5 w-3.5" />
                        ) : (
                          <Copy className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </div>
                    <RangerMarkdownContent content={message.content} className="text-sm text-foreground" />
                    {message.functionCalls && message.functionCalls.length > 0 && (
                      <div className="mt-2 flex flex-col items-start gap-1.5">
                        {message.functionCalls.map((call, i) => (
                          <RangerFunctionResultCard key={i} call={call} />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="flex items-center gap-1.5 py-1">
                <Bot className="h-3.5 w-3.5 text-adventure-600" />
                <span className="flex gap-1">
                  <span className="h-1.5 w-1.5 animate-typing-dot rounded-full bg-adventure-400 [animation-delay:0s]" />
                  <span className="h-1.5 w-1.5 animate-typing-dot rounded-full bg-adventure-400 [animation-delay:0.15s]" />
                  <span className="h-1.5 w-1.5 animate-typing-dot rounded-full bg-adventure-400 [animation-delay:0.3s]" />
                </span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}

        {error && (
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900/40 dark:bg-red-950/20">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
            <div className="flex-1">
              <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearError}
                className="mt-2 h-auto p-0 text-xs text-red-600 hover:text-red-700"
              >
                Dismiss
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-gray-200 bg-white p-3 dark:border-border dark:bg-background">
        <form onSubmit={handleSubmit} className="flex items-end gap-2">
          <Textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask Ranger about your trip..."
            disabled={loading}
            rows={1}
            maxLength={5000}
            className="max-h-[120px] min-h-[40px] flex-1 resize-none py-2.5"
          />
          <Button
            type="submit"
            disabled={!input.trim() || loading}
            size="icon"
            className="shrink-0 bg-adventure-600 text-white hover:bg-adventure-700"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </form>
      </div>
    </div>
  )
}
